/*
 *  -----------------------------------------------------------------------
 *    <copyright file="site-dashboard-summary-v2/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */
import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client/react';

import { ExtraMenuItemPosition } from '../../core/grid-header/grid-menu/grid-menu.interface';
import { ObsRowAction } from '../observations-grid-v2.interface';
import { SiteDashboardSummaryV2Props } from './site-dashboard-summary-v2.interface';
import { useTranslation, useBoolean } from 'hooks';
import { GET_CM_OBSERVATIONS_AND_ACTIONS } from 'queries/central-monitoring';
import {
    CentralMonitoringActionFields,
    CentralMonitoringObservationAndActionFields,
} from 'queries/generated/fragments.generated';
import { DashboardType } from 'types/graphql-global-types';
import { filterByType, getPanelName } from 'utils/cm-observations.service';
import { findAtLeastOneTrueValue } from 'utils/shared';
import ObservationsGridV2 from 'views/components/central-monitoring/observations-grid-v2';
import ChartsWrapper from 'views/components/central-monitoring/site-dashboard-summary/charts-wrapper';
import ReviewSection from 'views/components/central-monitoring/site-dashboard-summary/review-section';
import SummarySection from 'views/components/central-monitoring/site-dashboard-summary/summary-section';
import Loading from 'views/components/core/loading';
import Error from 'views/components/error/error';
import './styles.scss';

const SiteDashboardSummaryV2 = ({
    dataSets,
    datasetDate,
    onOpenEditReview,
    onOpenObservationsModal,
    onOpenActionModal,
    study,
    panels,
    show,
    canEdit,
    studyReviewInfo,
    datasetDates,
    studyData,
    scoringData,
    config,
    selectedSite,
    owners,
    studySites,
    handleDeleteObs,
    handleRestoreObs,
}: SiteDashboardSummaryV2Props) => {
    const { t } = useTranslation();

    const [siteInfo, setSiteInfo] = useState(null);
    const [siteScoreInfo, setSiteScoreInfo] = useState(null);
    const [siteConfig, setSiteConfig] = useState({} as any);
    const [observations, setObservations] = useState([]);
    const [actions, setActions] = useState([]);
    const [ownerOptions, setOwnerOptions] = useState([]);

    const { data, loading, error } = useQuery(GET_CM_OBSERVATIONS_AND_ACTIONS, {
        variables: {
            studyId: study?.id,
            siteId: siteInfo?.Fields['Site.id'],
            ...(datasetDate?.isCMR ? { timestamp: datasetDate.created } : {}),
        },
    });

    const showSummarySection = useBoolean(false);

    useEffect(() => {
        if (config) setSiteConfig(config.siteSummary);
    }, [config]);

    useEffect(() => {
        if (siteConfig)
            showSummarySection.setValue(
                findAtLeastOneTrueValue({ ...siteConfig, actionChart: false, observationChart: false }) ||
                    datasetDate?.isCMR,
            );
        if (selectedSite && studyData)
            setSiteInfo(studyData.sites.find((el: any) => el.Fields['Site.id'] === selectedSite.id));
        if (selectedSite && scoringData) {
            setSiteScoreInfo(scoringData.siteScores.find((el: any) => el.siteId === selectedSite.id));
        }
    }, [siteConfig, selectedSite]);

    useEffect(() => {
        setOwnerOptions([...owners]);
    }, [owners]);

    useEffect(() => {
        if (data?.centralMonitoringObservations && studyData && datasetDate) {
            const newArray: any[] = [];
            const array = data.centralMonitoringObservations
                .filter((observation: CentralMonitoringObservationAndActionFields) =>
                    filterByType(observation, 'site', datasetDate, datasetDates),
                )
                .map((observation: CentralMonitoringObservationAndActionFields) => {
                    const _activeActions = observation.actions?.filter(
                        (action: CentralMonitoringActionFields) => !action.deleted,
                    );
                    const _recentActionDate = _activeActions
                        .map((action: any) => action.requestDate)
                        .sort()
                        .reverse()[0];
                    const numberOfOpenActions = _activeActions.filter((action: any) => !action.completedDate);
                    const owner: any = observation.ownerId
                        ? ownerOptions.find((el: any) => el.id === observation.ownerId)
                        : null;
                    const _site =
                        studyData.sites?.find((item: any) => item.Fields['Site.id'] === observation.siteId)?.Fields[
                            'Site.name'
                        ] ?? null;
                    newArray.push(...observation.actions);
                    return {
                        ...observation,
                        confirmedCause: observation.confirmedCause ?? '',
                        comments: observation.comments ?? '',
                        potentialCause: observation.potentialCause ?? '',
                        _recentActionDate: _recentActionDate ? new Date(_recentActionDate) : null,
                        _openAction: numberOfOpenActions.length,
                        _numberOfActions: _activeActions.length,
                        _owner: owner,
                        _ownerLabel: owner?.text ?? '',
                        _datasetDate: observation.dataSet?.dataCutDate ?? null,
                        _panelName: getPanelName(observation.panel, observation.type),
                        _isClosedDateReadOnly:
                            observation.actions.findIndex((el: any) => !el.completedDate && !el.deleted) >= 0,
                        _site,
                        ...(observation.deleted && observation.audit
                            ? {
                                  _deleteReason: observation.audit.reason,
                                  _deleteDate: observation.audit.created,
                                  _deletedBy: `${observation.audit.userFirstName} ${observation.audit.userLastName}`,
                              }
                            : {}),
                        actions: observation.actions.map((action: CentralMonitoringActionFields) => {
                            const newOwner: any = action.ownerId
                                ? ownerOptions.find((el: any) => el.id === action.ownerId)
                                : null;
                            return {
                                ...action,
                                observationId: observation.id,
                                _obsDeleted: observation.deleted,
                                _owner: newOwner,
                                _site,
                                ownerLabel: newOwner?.text || '',
                                _isCompletedDateReadOnly: Boolean(observation.closedDate),
                                ...(action.deleted && action.audit
                                    ? {
                                          _deleteReason: action.audit.reason,
                                          _deleteDate: action.audit.created,
                                          _deletedBy: `${action.audit.userFirstName} ${action.audit.userLastName}`,
                                      }
                                    : {}),
                            };
                        }),
                    };
                });
            setObservations(array);
            setActions(newArray);
        }
    }, [data, studyData, ownerOptions, datasetDate, siteInfo, datasetDates]);

    const rowActions: ObsRowAction = (item) => {
        if (item.deleted) {
            return [
                {
                    label: 'cm:restoreObservation',
                    icon: ['fal', 'undo'],
                    onClick: () => handleRestoreObs(item),
                    enabled: canEdit && !datasetDate?.isCMR,
                },
            ];
        }

        return [
            {
                label: 'ra:editObservation',
                icon: ['fal', 'edit'],
                onClick: () => onOpenObservationsModal(item),
                enabled: canEdit && !datasetDate?.isCMR,
            },
            {
                label: 'ra:assignAction',
                icon: ['fal', 'link'],
                onClick: () => onOpenActionModal(null, item),
                enabled: canEdit && !item.closedDate && !datasetDate?.isCMR,
            },
            {
                label: 'cm:deleteObservation',
                icon: ['fal', 'trash'],
                onClick: () => handleDeleteObs(item),
                enabled: canEdit && !datasetDate?.isCMR,
            },
        ];
    };

    const siteId = siteInfo?.Fields['Site.id'];
    const siteReview = studySites?.find((review: any) => review.siteId === siteId) ?? {};

    if (!studyData && !siteInfo && !selectedSite && (!datasetDate || !Object.keys(datasetDate).length))
        return <h1>{t('admin:noDataAvailable')}</h1>;

    if ((!studyData || !siteInfo) && show) return <h1>{t('admin:noDataAvailable')}</h1>;

    if (error) return <Error error={error} />;

    if (!selectedSite) return <h1>{t('cm:noSiteSelected')}</h1>;

    if (!siteInfo && !studyData && !selectedSite) return <h1>{t('cm:noDataForSelectedDataset')}</h1>;

    return (
        <>
            {showSummarySection.value && (
                <SummarySection siteConfig={siteConfig} siteInfo={siteInfo} datasetDate={datasetDate} />
            )}
            <ReviewSection
                datasetDate={datasetDate}
                onOpenEditReview={onOpenEditReview}
                canEdit={canEdit}
                siteReview={siteReview}
                studyReviewInfo={studyReviewInfo}
                siteScoreInfo={siteScoreInfo}
            />
            <ChartsWrapper
                actions={actions}
                observations={observations}
                siteInfo={siteInfo}
                panels={panels}
                dataSets={dataSets?.dataSets ?? []}
                scoringData={scoringData}
                datasetDate={datasetDate}
                siteConfig={siteConfig}
            />
            {loading ? (
                <Loading inside />
            ) : (
                <ObservationsGridV2
                    className="opra-grid--not-single-item"
                    title={t('ra:observationsAndActions')}
                    observations={observations}
                    study={{ ...study, members: [...ownerOptions] }}
                    rowActions={rowActions}
                    canEdit={canEdit}
                    isCMR={datasetDate?.isCMR}
                    type={DashboardType.SITE}
                    hideCheckbox
                    extraMenuItems={[
                        {
                            position: ExtraMenuItemPosition.expandMenu,
                            customClassName: 'grid-header__item--always-visible',
                            expandMenuItems: [
                                {
                                    label: 'ra:addObservation',
                                    icon: ['fal', 'plus'],
                                    onClick: () => onOpenObservationsModal(),
                                    enabled: canEdit && !datasetDate?.isCMR,
                                },
                            ],
                        },
                    ]}
                />
            )}
        </>
    );
};

export default SiteDashboardSummaryV2;
